// src/contexts/CoreAppProvider.tsx
"use client";

import React, { ReactNode, useMemo } from 'react';
import type { Document } from '@/types';
import type { AccessControl } from '@/lib/firebase';
import { SessionProvider } from './SessionContext';
import { AssistantProvider } from './AssistantContext';
import { ToolProvider } from './ToolContext';

interface CoreAppProviderProps {
  children: ReactNode;
  assistantId: string;
  assistantConfig: {
    name: string;
    avatar: string;
    initialMessage?: string;
    initialInstructions: string;
    initialDocuments: Document[];
    tools: string[];
    toolConfigs: Record<string, Record<string, any>>;
    templateId?: string;
    accessControl?: AccessControl;
    adminEmail?: string;
    senderName?: string;
  };
}

/**
 * Level 1: Core Application Context
 * Consolidates Session, Assistant, and Tool configurations
 * Reduces nesting by combining static configuration contexts
 */
export const CoreAppProvider: React.FC<CoreAppProviderProps> = ({
  children,
  assistantId,
  assistantConfig
}) => {
  // Memoize assistant config for AssistantProvider to prevent re-renders
  const memoizedAssistantConfig = useMemo(() => ({
    assistantId,
    name: assistantConfig.name,
    avatar: assistantConfig.avatar,
    initialMessage: assistantConfig.initialMessage || "Hello!",
    initialInstructions: assistantConfig.initialInstructions,
    initialDocuments: assistantConfig.initialDocuments,
    tools: assistantConfig.tools,
    toolConfigs: assistantConfig.toolConfigs,
    templateId: assistantConfig.templateId,
    accessControl: assistantConfig.accessControl,
    adminEmail: assistantConfig.adminEmail,
    senderName: assistantConfig.senderName
  }), [assistantId, assistantConfig]);

  return (
    <SessionProvider>
      <AssistantProvider config={memoizedAssistantConfig}>
        <ToolProvider toolConfigs={assistantConfig.toolConfigs}>
          {children}
        </ToolProvider>
      </AssistantProvider>
    </SessionProvider>
  );
};